/**
 * LotoWheel Modal — Tab Acceso (whitelist de visibilidad por operador)
 * Se extiende sobre LotowheelModule declarado en lotowheel.js
 */

import { LotowheelModule } from './lotowheel.js';
import { App, Api } from '../../../admin.js';
import { ROL_ADMIN } from '@shared/config/business-constants.js';
import { escapeHtmlParaInyeccionSegura } from '../../../utils/escape-html-para-inyeccion-segura.js';
import { t } from '../../../services/i18n-admin.js';

Object.assign(LotowheelModule, {
    // ====================================================================
    // ACCESOS — Whitelist de visibilidad de sorteos en el admin
    // ====================================================================

    /**
     * Muestra u oculta el tab "Acceso" según rol y propiedad del sorteo.
     * Superadmin → siempre visible.
     * Propietario del sorteo (config.nodo_id === currentUser.nodo_id) → visible.
     * Cualquier otro → oculto.
     */
    _maybeShowTabAcceso(sorteoConfig) {
        const tabAcceso = document.querySelector('#modalLotowheelConfig [data-tab="acceso"]');
        if (!tabAcceso) return;
        const esSuperadmin = App.currentUser?.rol === ROL_ADMIN.SUPERADMIN;
        const esPropietario = sorteoConfig?.nodo_id && sorteoConfig.nodo_id === App.currentUser?.nodo_id;
        tabAcceso.style.display = esSuperadmin || esPropietario ? '' : 'none';
    },

    /**
     * Carga la pestaña Acceso para el sorteo con id dado.
     *
     * Sorteos globales (nodo_id = null): superadmin gestiona qué operadores los ven.
     * Sorteos de operador (nodo_id != null): el propietario gestiona qué suboperadores los ven.
     */
    async loadAccesoPanel(sorteoId) {
        const isSuperadmin = App.currentUser?.rol === ROL_ADMIN.SUPERADMIN;
        const config = this.currentSorteoConfig;
        const esSorteoGlobal = config && !config.nodo_id;
        const esPropietario = config?.nodo_id && config.nodo_id === App.currentUser?.nodo_id;

        const msgWrapper = document.getElementById('accesoSoloSuperadmin');
        const msgEl = document.getElementById('accesoSoloSuperadminMsg');
        const panel = document.getElementById('accesoPanel');

        // Caso 1: Superadmin mirando sorteo de operador — informativo, sin panel
        if (isSuperadmin && !esSorteoGlobal) {
            const propietario = config?.nodo_nombre || 'nodo #' + config?.nodo_id;
            if (msgEl)
                msgEl.innerHTML =
                    'Este sorteo pertenece a <strong>' +
                    propietario +
                    '</strong>. El propietario puede gestionar qué suboperadores tienen acceso.';
            if (msgWrapper) msgWrapper.style.display = '';
            if (panel) panel.style.display = 'none';
            return;
        }

        // Caso 2: Sorteo global → solo superadmin. Sorteo de operador → solo el propietario.
        // Si llegamos aquí y no es ninguno de los dos, el tab no debería estar visible.
        if (!isSuperadmin && !esPropietario) {
            if (msgEl) msgEl.textContent = t('Sin permisos para gestionar accesos de este sorteo.');
            if (msgWrapper) msgWrapper.style.display = '';
            if (panel) panel.style.display = 'none';
            return;
        }

        // Panel completo — superadmin en sorteo global, o propietario en su sorteo
        if (msgWrapper) msgWrapper.style.display = 'none';
        if (panel) panel.style.display = '';

        // Cargar nodos disponibles:
        // - Superadmin (sorteo global): todos los operadores excepto el nodo raíz
        // - Propietario: solo sus descendientes directos e indirectos (tipo operador)
        try {
            let todos = [];
            if (isSuperadmin) {
                const nodosResp = await Api.callAdmin('/admin/nodos?tipo=operador', 'GET');
                const raw = Array.isArray(nodosResp) ? nodosResp : nodosResp?.nodos || [];
                // Excluir nodo raíz (parent_id = null)
                todos = raw.filter(n => n.parent_id !== null);
            } else {
                // Propietario: descendientes de su nodo (excluye el propio nodo raíz del operador)
                const nodosResp = await Api.callAdmin(
                    `/admin/nodos?parent_id=${App.currentUser.nodo_id}&incluir_descendientes=1&tipo=operador`,
                    'GET'
                );
                todos = Array.isArray(nodosResp) ? nodosResp : nodosResp?.nodos || [];
            }
            this._accesoNodos = todos;
        } catch (e) {
            this._accesoNodos = [];
        }

        // Edge case: propietario sin suboperadores → mensaje claro
        if (!isSuperadmin && this._accesoNodos.length === 0) {
            if (msgEl)
                msgEl.innerHTML =
                    t('No tienes suboperadores configurados.') + '<br>' +
                    '<span style="color:var(--text-muted);font-size:12px;">' + t('Cuando tengas suboperadores podrás restringir cuáles acceden a este sorteo.') + '</span>';
            if (msgWrapper) msgWrapper.style.display = '';
            if (panel) panel.style.display = 'none';
            return;
        }

        // Si ya tenemos datos cacheados para este sorteo, solo re-renderizar — no perder cambios del usuario
        if (this._cachedModalSorteoId === sorteoId && this._accesoNodos.length > 0) {
            this._renderAccesoNodoList(this._accesoNodos);
            return;
        }

        // Cargar accesos actuales del sorteo desde API.
        // Modelo visual: todos marcados = abierto (whitelist vacía en BD).
        //   Si la BD tiene whitelist → marcar solo esos.
        //   Si la BD tiene lista vacía → marcar TODOS (abierto a todos).
        let whitelistEnBd = [];
        if (sorteoId) {
            try {
                const accesosResp = await Api.callAdmin(`/admin/sorteos/${sorteoId}/accesos`, 'GET');
                whitelistEnBd = accesosResp?.accesos || [];
            } catch (e) {
                // Sin accesos previos
            }
        }

        if (whitelistEnBd.length === 0) {
            // Sin restricciones → todos marcados
            this._accesoNodoIdsSeleccionados = new Set(this._accesoNodos.map(n => n.id));
        } else {
            // Whitelist activa → marcar solo los autorizados
            this._accesoNodoIdsSeleccionados = new Set();
            for (let i = 0; i < whitelistEnBd.length; i++) {
                this._accesoNodoIdsSeleccionados.add(whitelistEnBd[i].nodo_id);
            }
        }

        this._renderAccesoNodoList(this._accesoNodos);
    },

    /**
     * Actualiza el badge de estado visible en el panel.
     * "Abierto a todos" = todos los nodos están marcados (whitelist vacía en BD).
     * "Restringido" = al menos uno desmarcado.
     * "Cerrado a todos" = ninguno marcado (caso extremo).
     */
    _renderAccesoEstadoBadge() {
        const badge = document.getElementById('accesoEstadoBadge');
        if (!badge) return;
        const total = this._accesoNodos.length;
        const marcados = this._accesoNodoIdsSeleccionados.size;

        if (marcados === 0) {
            badge.innerHTML =
                '<span style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:6px;background:rgba(239,83,80,0.12);border:1px solid rgba(239,83,80,0.3);font-size:13px;color:#ef5350;">' +
                '🚫 <strong>' + t('Cerrado a todos') + '</strong> — ' + t('ningún operador puede ver este sorteo.') +
                '</span>';
        } else if (marcados >= total) {
            badge.innerHTML =
                '<span style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:6px;background:rgba(76,175,80,0.12);border:1px solid rgba(76,175,80,0.3);font-size:13px;color:#4caf50;">' +
                '🔓 <strong>' + t('Abierto a todos') + '</strong> — ' + t('cualquier operador puede ver este sorteo.') +
                '</span>';
        } else {
            const excluidos = total - marcados;
            badge.innerHTML =
                '<span style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;border-radius:6px;background:rgba(255,152,0,0.12);border:1px solid rgba(255,152,0,0.3);font-size:13px;color:#ff9800;">' +
                '🔒 <strong>' + t('Restringido — ') +
                excluidos +
                (excluidos !== 1 ? t(' operadores') : t(' operador')) +
                (excluidos !== 1 ? t(' excluidos') : t(' excluido')) +
                '</strong> — ' + t('solo los marcados (y sus descendientes) lo verán.') +
                '</span>';
        }
    },

    _renderAccesoNodoList(nodos) {
        const list = document.getElementById('accesoNodoList');
        if (!list) return;

        // Actualizar badge de estado antes de renderizar la lista
        this._renderAccesoEstadoBadge();

        if (!nodos || nodos.length === 0) {
            list.innerHTML =
                '<div style="color:var(--text-muted);font-size:13px;padding:8px;">' + t('No hay operadores disponibles.') + '</div>';
            return;
        }

        let html = '';
        for (let i = 0; i < nodos.length; i++) {
            const n = nodos[i];
            const checked = this._accesoNodoIdsSeleccionados.has(n.id) ? 'checked' : '';
            html += `
                <label class="row-hover-soft" style="display:flex;align-items:center;gap:10px;padding:6px 4px;cursor:pointer;border-radius:4px;">
                  <input type="checkbox" value="${n.id}" ${checked}
                    data-action="lotowheel:onAccesoNodoToggle" data-id="${n.id}"
                    style="width:16px;height:16px;cursor:pointer;">
                  <span style="flex:1;font-size:13px;">${escapeHtmlParaInyeccionSegura(n.nombre)}</span>
                  <span style="font-size:11px;color:var(--text-muted);">${escapeHtmlParaInyeccionSegura(n.tipo)}</span>
                </label>`;
        }
        list.innerHTML = html;
    },

    onAccesoNodoToggle(nodoId, isChecked) {
        if (isChecked) {
            this._accesoNodoIdsSeleccionados.add(nodoId);
        } else {
            this._accesoNodoIdsSeleccionados.delete(nodoId);
        }
        // Actualizar badge en tiempo real al cambiar checks
        this._renderAccesoEstadoBadge();
    },

    onAccesoSearch(query) {
        const q = query.toLowerCase().trim();
        if (!q) {
            this._renderAccesoNodoList(this._accesoNodos);
            return;
        }
        const filtered = [];
        for (let i = 0; i < this._accesoNodos.length; i++) {
            if (this._accesoNodos[i].nombre.toLowerCase().includes(q)) {
                filtered.push(this._accesoNodos[i]);
            }
        }
        this._renderAccesoNodoList(filtered);
    },

    clearAccesoNodoSearch() {
        const input = document.getElementById('accesoNodoSearch');
        if (input) input.value = '';
        this._renderAccesoNodoList(this._accesoNodos);
    },

    /** Marca todos los operadores → abierto a todos */
    selectAllAccesoNodos() {
        this._accesoNodoIdsSeleccionados = new Set(this._accesoNodos.map(n => n.id));
        this._renderAccesoNodoList(this._accesoNodos);
    },

    /** Desmarca todos los operadores → cerrado a todos */
    clearAllAccesoNodos() {
        this._accesoNodoIdsSeleccionados.clear();
        this._renderAccesoNodoList(this._accesoNodos);
    },

    async saveAccesoNodos() {
        const id = document.getElementById('lotowheelConfigId').value;
        if (!id) return;

        // Todos marcados → lista vacía en BD (abierto a todos)
        const todosSeleccionados =
            this._accesoNodos.length > 0 && this._accesoNodoIdsSeleccionados.size >= this._accesoNodos.length;
        const nodoIds = todosSeleccionados ? [] : Array.from(this._accesoNodoIdsSeleccionados);

        const result = await Api.callAdmin(`/admin/sorteos/${id}/accesos`, 'PUT', { nodo_ids: nodoIds });
        if (result !== null) {
            const msg =
                nodoIds.length === 0
                    ? t('Acceso abierto — sorteo visible para todos')
                    : t('Accesos guardados: ') + nodoIds.length + ' ' + t('operador(es) autorizados');
            App.toast(msg);
        }
    },
});
